import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { FaRegUser, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import {useAuth} from '../../../context/AuthContext.js';
import { useLanguage } from '../../../context/LanguageContext.js';
import ButtonWithLoader from "../../../components/Button/ButtonLoader/ButtonLoader.js";
import GoogleButton from '../GoogleButton/GoogleButton.jsx';
import { authAPI } from '../../../api/api.js';
import { resolvePostLoginPath } from '../../../Utils/postLoginNavigation.js';
import { getGoogleAuthRedirectError } from '../../../Utils/authErrors.js';
import AuthBrand from '../AuthBrand/AuthBrand.jsx';

function Login() {
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const {login} = useAuth();
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        const urlError = getGoogleAuthRedirectError(searchParams, t);
        if (urlError) {
            setError(urlError);
        }
    }, [searchParams, t]);

    if (searchParams.get('session') === 'expired') {
        return <Navigate to="/session-expired" replace/>;
    }

    const handleGoogleAuth = () => {
        window.location.href = authAPI.googleAuth({ intent: 'login' });
    };

    const handleChange = (e) => {
        setFormData({...formData, [e.target.name]: e.target.value});
        setError('');
    };

    const performLogin = async (email, password) => {
        setError('');
        setLoading(true);

        try {
            const result = await login(email, password);

            if (result.requiresTwoFactor) {
                navigate('/2fa-verify', {
                    state: {
                        tempToken: result.tempToken,
                        requiresPasswordChange: !!result.requiresPasswordChange,
                    },
                });
                return;
            }

            navigate(await resolvePostLoginPath({
                passwordChangeRequired: result.requiresPasswordChange,
            }));
        } catch (err) {
            const data = err.response?.data;
            if (data?.code === 'EMAIL_NOT_VERIFIED') {
                const email = data.email || formData.email;
                const params = new URLSearchParams();
                if (email) {
                    params.set('email', email);
                }
                navigate(`/verify-email-code?${params.toString()}`, {
                    replace: true,
                    state: {
                        email,
                        maskedEmail: data.maskedEmail,
                        codeSent: !!data.codeSent,
                        fromLogin: true,
                    },
                });
                return;
            }
            const errorMsg =
                (typeof data?.message === 'string' && data.message.trim()) ||
                data?.errors?.[0]?.msg ||
                err.message ||
                t('auth.login.unexpectedError');
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        await performLogin(formData.email, formData.password);
    };

    return (
      <main className="auth-page">
        <AuthBrand corner href="https://www.quazbit.com" />
        <div className="auth-shell container">
          <form className="auth-card" onSubmit={handleSubmit}>
            <h1 className="auth-title">{t('auth.login.eyebrow')}</h1>

            <GoogleButton onClick={handleGoogleAuth} />

            <div className="auth-fields">
              <div className="auth-field">
                <FaRegUser className="auth-field-icon" />
                <div className="auth-field-control">
                  <input
                    type="email"
                    required
                    className="auth-input"
                    placeholder=" "
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                  <label className="auth-input-label">{t('auth.fields.email')}</label>
                </div>
              </div>

              <div className="auth-field">
                <FaLock className="auth-field-icon" />
                <div className="auth-field-control">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="auth-input"
                    placeholder=" "
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                  />
                  <label className="auth-input-label">{t('auth.fields.password')}</label>
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={
                      showPassword ? t('auth.fields.hidePassword') : t('auth.fields.showPassword')
                    }
                    className="auth-password-toggle"
                  >
                    {showPassword ? (
                      <FaEyeSlash className="auth-password-icon" />
                    ) : (
                      <FaEye className="auth-password-icon" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="auth-meta">
              <div className="auth-meta-group">
                <input id="remember-me" type="checkbox" className="auth-checkbox" />
                <label htmlFor="remember-me" className="auth-checkbox-label">
                  {t('auth.login.rememberMe')}
                </label>
              </div>
              <span className="auth-link" onClick={() => navigate('/forgot-password')}>
                {t('auth.login.forgotPassword')}
              </span>
            </div>

            <div className="auth-action-box">
              <ButtonWithLoader text={t('auth.login.submit')} loading={loading} type="submit" />
            </div>

            <p className="auth-link-row">
              {t('auth.login.noAccount')}
              <strong className="auth-link" onClick={() => navigate('/register')}>
                {' '}
                {t('auth.login.register')}
              </strong>
            </p>

            {error && <p className="auth-message auth-message--error">{error}</p>}
          </form>
        </div>
      </main>
    );
}

export default Login;
